// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventExplorerPage
 * @package @geenius/analytics/react
 * @description Page-level React composition for filtering, inspecting, and
 * exporting analytics events.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import { buildAnalyticsSeoMeta } from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/react";
import type React from "react";
import { useMemo, useState } from "react";
import { EventTable, type EventTableFilters } from "../components/EventTable";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import {
  ANALYTICS_MOTION_DELAYS,
  FadeIn,
  getFadeInTransition,
} from "../lib/motion";
import type { AnalyticsSeoMeta } from "./AnalyticsDashboardPage";

export type EventExplorerFilters = EventTableFilters;

const EVENT_TYPE_TRANSLATION_KEYS: Readonly<Record<EventType, string>> = {
  page_view: "analytics.events.types.pageView",
  click: "analytics.events.types.click",
  form_submit: "analytics.events.types.formSubmit",
  custom: "analytics.events.types.custom",
};

/**
 * Props for rendering the React event explorer page.
 *
 * @property events Event rows available to browse and export.
 * @property initialFilters Optional initial filters for the local controls.
 * @property onFiltersChange Called whenever local filters change.
 * @property onExport Called with the current filtered event rows.
 * @property isLoading Whether the table should render loading placeholders.
 * @property error Optional page-level error message or error object.
 * @property onRetry Optional retry handler rendered with the error state.
 * @property maxRows Maximum number of table rows to render.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property className Optional class name merged into the page wrapper.
 */
export interface EventExplorerPageProps {
  events?: AnalyticsEvent[];
  initialFilters?: EventExplorerFilters;
  onFiltersChange?: (filters: EventExplorerFilters) => void;
  onExport?: (events: AnalyticsEvent[]) => void;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  maxRows?: number;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => React.ReactNode;
  className?: string;
}

function toDateInputValue(timestamp?: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().slice(0, 10);
}

function fromDateInputValue(
  value: string,
  boundary: "start" | "end",
): number | undefined {
  if (!value) return undefined;
  const date = new Date(
    `${value}T${boundary === "start" ? "00:00:00" : "23:59:59"}`,
  );
  return Number.isNaN(date.getTime()) ? undefined : date.getTime();
}

function includesText(value: string | undefined, query: string): boolean {
  return value?.toLowerCase().includes(query) ?? false;
}

function eventMatchesFilters(
  event: AnalyticsEvent,
  filters: EventExplorerFilters,
): boolean {
  if (filters.types?.length && !filters.types.includes(event.type))
    return false;
  if (filters.startTime && event.timestamp < filters.startTime) return false;
  if (filters.endTime && event.timestamp > filters.endTime) return false;
  if (filters.device && event.device.device !== filters.device) return false;

  const urlQuery = filters.url?.trim().toLowerCase();
  if (urlQuery && !includesText(event.url, urlQuery)) return false;

  const userQuery = filters.userId?.trim().toLowerCase();
  if (userQuery && !includesText(event.userId, userQuery)) return false;

  const sessionQuery = filters.sessionId?.trim().toLowerCase();
  if (sessionQuery && !includesText(event.sessionId, sessionQuery))
    return false;

  const searchQuery = filters.search?.trim().toLowerCase();
  if (searchQuery) {
    const searchable = [
      event.name,
      event.type,
      event.url,
      event.userId,
      event.sessionId,
      event.device.browser,
      event.device.os,
      JSON.stringify(event.properties ?? {}),
    ].join(" ");

    if (!searchable.toLowerCase().includes(searchQuery)) return false;
  }

  return true;
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

function EventExplorerError({
  error,
  onRetry,
}: {
  error: Error | string;
  onRetry?: () => void;
}): React.ReactElement {
  const copy = useAnalyticsCopy();

  return (
    <div role="alert" data-analytics-event-explorer-error>
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="text-lg">
            {copy.t("analytics.events.errors.loadTitle")}
          </CardTitle>
          <CardDescription>{getErrorMessage(error)}</CardDescription>
        </CardHeader>
        {onRetry && (
          <CardContent className="pt-4">
            <Button type="button" variant="secondary" onClick={onRetry}>
              {copy.t("analytics.actions.retry")}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

/**
 * Renders an event explorer with filters, property inspection, and export.
 *
 * @param props Event rows, initial filters, and export callback.
 * @returns A React page composition for event exploration workflows.
 */
export function EventExplorerPage({
  events = [],
  initialFilters,
  onFiltersChange,
  onExport,
  isLoading,
  error,
  onRetry,
  maxRows = 25,
  renderSeoMeta,
  className,
}: EventExplorerPageProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const [filters, setFilters] = useState<EventExplorerFilters>(
    initialFilters ?? {},
  );
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const seoMeta = buildAnalyticsSeoMeta({
    title: "Event Explorer",
    description:
      "Browse, filter, inspect, and export analytics events for product analysis.",
    path: "/analytics/events",
    keywords: ["analytics", "events", "filters", "export"],
  });
  const filteredEvents = useMemo(
    () => events.filter((event) => eventMatchesFilters(event, filters)),
    [events, filters],
  );
  const inspectedEvent =
    filteredEvents.find((event) => event.id === selectedEventId) ??
    filteredEvents[0] ??
    null;

  const updateFilters = (patch: Partial<EventExplorerFilters>): void => {
    const next = { ...filters, ...patch };
    setFilters(next);
    onFiltersChange?.(next);
  };

  return (
    <>
      {renderSeoMeta ? renderSeoMeta(seoMeta) : null}
      <div
        className={cn(
          "flex min-h-screen flex-col gap-6 bg-background p-6 text-foreground",
          className,
        )}
        data-analytics-page="event-explorer"
      >
        <FadeIn transition={getFadeInTransition()}>
          <header className="flex flex-col gap-1" data-analytics-page-header>
            <h1 className="text-3xl font-bold tracking-tight">
              Event Explorer
            </h1>
            <p className="text-sm text-muted-foreground">
              Filter tracked events, inspect properties, and export the current
              result set.
            </p>
          </header>
        </FadeIn>

        {error ? (
          <EventExplorerError error={error} onRetry={onRetry} />
        ) : (
          <>
            <FadeIn
              transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.stats)}
            >
              <Card data-analytics-event-filters>
                <CardHeader className="pb-0">
                  <CardTitle data-analytics-section-title>Filters</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 pt-4 sm:grid-cols-2 xl:grid-cols-4">
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.search")}</span>
                    <input
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      value={filters.search ?? ""}
                      onChange={(event) =>
                        updateFilters({ search: event.currentTarget.value })
                      }
                      placeholder={copy.t(
                        "analytics.events.filters.searchPlaceholder",
                      )}
                    />
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.type")}</span>
                    <select
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      value={filters.types?.[0] ?? ""}
                      onChange={(event) =>
                        updateFilters({
                          types: event.currentTarget.value
                            ? [event.currentTarget.value as EventType]
                            : undefined,
                        })
                      }
                    >
                      <option value="">
                        {copy.t("analytics.events.filters.allTypes")}
                      </option>
                      {(
                        ["page_view", "click", "form_submit", "custom"] as const
                      ).map((type) => (
                        <option key={type} value={type}>
                          {copy.t(EVENT_TYPE_TRANSLATION_KEYS[type])}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.url")}</span>
                    <input
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      value={filters.url ?? ""}
                      onChange={(event) =>
                        updateFilters({ url: event.currentTarget.value })
                      }
                      placeholder="/pricing"
                    />
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.user")}</span>
                    <input
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      value={filters.userId ?? ""}
                      onChange={(event) =>
                        updateFilters({ userId: event.currentTarget.value })
                      }
                      placeholder="user_"
                    />
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.session")}</span>
                    <input
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      value={filters.sessionId ?? ""}
                      onChange={(event) =>
                        updateFilters({ sessionId: event.currentTarget.value })
                      }
                      placeholder="ses_"
                    />
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.device")}</span>
                    <select
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      value={filters.device ?? ""}
                      onChange={(event) =>
                        updateFilters({
                          device: event.currentTarget.value
                            ? (event.currentTarget
                                .value as DeviceInfo["device"])
                            : undefined,
                        })
                      }
                    >
                      <option value="">
                        {copy.t("analytics.events.filters.allDevices")}
                      </option>
                      {(["desktop", "tablet", "mobile"] as const).map(
                        (device) => (
                          <option key={device} value={device}>
                            {copy.deviceLabel(device)}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.from")}</span>
                    <input
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      type="date"
                      value={toDateInputValue(filters.startTime)}
                      onChange={(event) =>
                        updateFilters({
                          startTime: fromDateInputValue(
                            event.currentTarget.value,
                            "start",
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                    <span>{copy.t("analytics.events.filters.to")}</span>
                    <input
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                      type="date"
                      value={toDateInputValue(filters.endTime)}
                      onChange={(event) =>
                        updateFilters({
                          endTime: fromDateInputValue(
                            event.currentTarget.value,
                            "end",
                          ),
                        })
                      }
                    />
                  </label>
                </CardContent>
              </Card>
            </FadeIn>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)]">
              <FadeIn
                transition={getFadeInTransition(
                  ANALYTICS_MOTION_DELAYS.overview,
                )}
              >
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <span
                    className="text-sm font-medium text-muted-foreground"
                    data-analytics-event-count
                  >
                    {filteredEvents.length.toLocaleString()} events
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={filteredEvents.length === 0}
                    onClick={() => onExport?.(filteredEvents)}
                  >
                    Export
                  </Button>
                </div>
                <div
                  data-analytics-event-table-loading={isLoading || undefined}
                >
                  <EventTable
                    events={filteredEvents}
                    isLoading={isLoading}
                    maxRows={maxRows}
                    showFilters={false}
                  />
                </div>
              </FadeIn>

              <FadeIn
                transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.events)}
              >
                <Card data-analytics-event-inspector>
                  <CardHeader className="pb-0">
                    <CardTitle data-analytics-section-title>
                      {copy.t("analytics.events.inspector.title")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4 pt-4">
                    {isLoading ? (
                      <>
                        <Skeleton className="h-9 w-full" />
                        <Skeleton className="h-36 w-full" />
                      </>
                    ) : filteredEvents.length === 0 ? (
                      <EmptyState
                        bordered={false}
                        compact
                        title={copy.t("analytics.events.inspector.empty")}
                      />
                    ) : (
                      <>
                        <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                          <span>
                            {copy.t("analytics.events.inspector.selectedEvent")}
                          </span>
                          <select
                            className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
                            value={inspectedEvent?.id ?? ""}
                            onChange={(event) =>
                              setSelectedEventId(event.currentTarget.value)
                            }
                          >
                            {filteredEvents.map((event) => (
                              <option key={event.id} value={event.id}>
                                {event.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <dl className="grid gap-3 text-sm">
                          <div className="grid gap-1">
                            <dt className="text-xs font-medium uppercase text-muted-foreground">
                              Session
                            </dt>
                            <dd>{inspectedEvent?.sessionId}</dd>
                          </div>
                          <div className="grid gap-1">
                            <dt className="text-xs font-medium uppercase text-muted-foreground">
                              User
                            </dt>
                            <dd>{inspectedEvent?.userId ?? "anonymous"}</dd>
                          </div>
                          <div className="grid gap-1">
                            <dt className="text-xs font-medium uppercase text-muted-foreground">
                              {copy.t("analytics.events.columns.device")}
                            </dt>
                            <dd>
                              {inspectedEvent
                                ? copy.deviceLabel(inspectedEvent.device.device)
                                : ""}
                            </dd>
                          </div>
                        </dl>
                        <pre className="max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-xs text-foreground">
                          {JSON.stringify(
                            inspectedEvent?.properties ?? {},
                            null,
                            2,
                          )}
                        </pre>
                      </>
                    )}
                  </CardContent>
                </Card>
              </FadeIn>
            </div>
          </>
        )}
      </div>
    </>
  );
}
